import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

// third-party
import _ from "lodash";

// project-imports
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";
import InterviewQuestionList from "./InterviewQuestionList";
import InterviewQuestionsDetailStencil from "./stencils/InterviewQuestionsDetailStencil";
import InterviewQuestionListAccordion from "./InterviewQuestionListAccordion";
import InterviewQuestionHeader from "./InterviewQuestionHeader";
import AISummaryBox from "./ai-summary-box/AISummaryBox";
import { GoogleAnalyticsEnum, pageView, triggerFunction } from "../../utils/google-analytics";
import { toUpperSnake } from "../../utils";
import InterviewQuestionCategory from "./interview-categories/InterviewQuestionCategory";
import InterviewRelatedSeries from "./interview-series/InterviewRelatedSeries";
import AboutAuthor from "../about/AboutAuthor";
import { useInterviewContext } from "../../context/interview_context";


export default function InterviewQuestionsDetail({ id }) {
    // Extracting variables from context
    const { interviewSeries, interviewQuestions, isLoading, isQuestionLoading } = useInterviewContext();

    //State initalization
    const [activeTab, setActiveTab] = useState(null);
    const [activeTabData, setActiveTabData] = useState([]);
    const [tabs, setTabs] = useState([]);
    const [seriesData, setSeriesData] = useState([]);
    const [selectedSeries, setSelectedSeries] = useState({});

    // property initialization
    const { series: seriesParam, category: categoryParam } = useParams();

    const [searchParams] = useSearchParams();
    const section = searchParams?.get('section') || '';

    // constant variables
    const categoryName = seriesData?.map((i) => i?.categoryId || "");
    const seriesName = seriesData?.map((i) => i?.name || "");


    useEffect(() => {
        // Constant categories
        const seriesList =
            interviewSeries && interviewSeries?.length ? interviewSeries : [];
        const qaList =
            interviewQuestions && interviewQuestions.length ? interviewQuestions : [];

        const filteredSeries = (seriesList || []).filter(
            (s) => s?.slug === seriesParam && s?.categoryId === categoryParam,
        );

        if (filteredSeries && filteredSeries.length) {
            setSelectedSeries(filteredSeries[0]);
        }

        let matchedData = [];

        if (interviewQuestions.length) {
            matchedData = filteredSeries.map((elm) => {
                return {
                    ...elm,
                    qaList: qaList?.filter((item) =>
                        item?.seriesMappings?.map(
                            (i) => i.seriesId === filteredSeries?.[0]?._id,
                        ),
                    ),
                };
            });

            // Set series data
            setSeriesData(matchedData);
        }

        let tabData = (matchedData?.map((elm) => elm?.sections) || []).flat();
        tabData = _.sortBy(tabData, "sortOrder");

        // Update Local State
        setTabs(tabData);

        if (!activeTab && interviewQuestions?.length && !section) {
            setActiveTab(tabData?.length ? tabData[0]?._id : null);
        } else if (!activeTab && interviewQuestions?.length && section) {
            setActiveTab(section);
        }
    }, [interviewSeries, interviewQuestions]);

    useEffect(() => {
        const qaList =
            interviewQuestions && interviewQuestions.length ? interviewQuestions : [];

        const filteredDataByTab = (tabs || [])
            .map((tab) => ({
                tab: tab?._id,
                questions: qaList
                    ?.filter((item) => {
                        const isMatchFound = item?.seriesMappings?.find(
                            (i) =>
                                i?.seriesId === selectedSeries?._id &&
                                i?.sectionId === activeTab,
                        );
                        return isMatchFound ? item : null;
                    })
                    .filter((i) => i !== null),
            }))
            .filter((i) => i?.tab === activeTab);

        const selectedTabData = (
            filteredDataByTab && filteredDataByTab?.length ? filteredDataByTab : []
        )
            .map((item) => {
                return item?.questions?.map((subItem) => {
                    const ansItems = subItem?.answer?.split("Shubham's insight:");
                    return {
                        ...subItem,
                        insight: ansItems && ansItems.length > 1 ? ansItems[1] : "",
                        answer: ansItems && ansItems.length > 0 ? ansItems[0] : "",
                    };
                });
            })
            .flat();
        if (filteredDataByTab?.length) {
            setActiveTabData(selectedTabData);
        }
    }, [activeTab]);

    const handleTab = (elm) => {
        try {
            setActiveTab(elm?._id);
            triggerFunction(
                GoogleAnalyticsEnum.BUTTON,
                `CATEGORY_${toUpperSnake(categoryName)}_SERIES_${toUpperSnake(seriesName)}_TAB_${toUpperSnake(elm?.name)}`,
            )
        } catch (error) {
            console.log("138 : InterviewQuestionDetail : Error in handleTab", error);
        }
    };

    if (isLoading || isQuestionLoading) {
        // Loading stencil
        return (
            <InterviewQuestionsDetailStencil tabs={tabs} qaList={interviewQuestions} seriesData={seriesData} />
        );
    }

    return (
        <>
            <SEO
                title={selectedSeries?.title}
                url={
                    process.env.PUBLIC_URL +
                    `/interview-questions/salesforce-revenue-cloud-interview-questions`
                }
            />
            {pageView(window.location.pathname, GoogleAnalyticsEnum.INTERVIEW_QUESTIONS_AND_ANSWER_DETAILS)}
            <Layout searchDisable={true}>
                {/* <BreadcrumbOne title={selectedSeries?.name} rootUrl="/" parentUrl="Interview Questions Home" currentUrl={selectedSeries?.name} /> */}
                <InterviewQuestionHeader data={seriesData} />
                <div className="edu-elements-area edu-section-gap bg-color-white interview-questions interview-questions-detail">
                    <div className="container mb--130">
                        <div className="tabs -side js-tabs">
                            <div className="row y-gap-40">
                                <div className="col-12 col-lg-4">
                                    <h5 class="widget-title">Topics</h5>
                                    <div className="tabs__controls js-tabs-controls">
                                        {(tabs || [])?.map((elm, i) => (
                                            <div key={i}>
                                                <Link
                                                    key={i}
                                                    onClick={() => handleTab(elm)}
                                                    className={`tabs__button text-dark js-tabs-button ${activeTab === elm?._id ? "is-active-2" : ""
                                                        } `}
                                                    type="button"
                                                >
                                                    {elm?.name}
                                                </Link>
                                            </div>
                                        ))}
                                    </div>

                                    <AISummaryBox data={selectedSeries?.aiSummary} currentSeries={seriesName} isLoading={false} classes="d-none d-lg-flex mt--40" />

                                    <InterviewQuestionCategory classes="d-none d-lg-block mt--40" />

                                    <InterviewRelatedSeries currentSeries={seriesName} classes="d-none d-lg-flex mt--40" />

                                    <AboutAuthor classes="d-none d-lg-flex mt--40" />
                                </div>

                                <div className="col-12 col-lg-8">
                                    <div className="tabs__content js-tabs-content">
                                        <div
                                            className={`tabs__pane -tab-item-1 ${activeTab ? "is-active-2" : ""}`}
                                        >
                                            <InterviewQuestionListAccordion
                                                data={activeTabData}
                                                activeTab={activeTab}
                                                tabs={tabs}
                                                handleTab={handleTab}
                                            />
                                            {/* <InterviewQuestionList data={activeTabData} /> */}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 mt--20">
                                    <AISummaryBox data={selectedSeries?.aiSummary} currentSeries={seriesName} isLoading={false} classes="d-flex d-lg-none mt--20" />
                                    <InterviewQuestionCategory classes="d-block d-lg-none mt--20" />
                                    <InterviewRelatedSeries currentSeries={seriesName} classes="d-flex d-lg-none mt--20" />
                                    <AboutAuthor classes="d-flex d-lg-none mt--20" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
